document.documentElement.classList.remove('no-js');

const sliderEdgeTolerance = 2;

const getSliderButtons = (slider) => {
  const section = slider.closest('.section-band');
  if (!section) return [];

  return [...section.querySelectorAll('[data-slider-button]')].filter((button) => {
    return !button.dataset.sliderTarget || button.dataset.sliderTarget === slider.id;
  });
};

const updateSliderButtons = (slider) => {
  const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
  const isAtStart = slider.scrollLeft <= sliderEdgeTolerance;
  const isAtEnd = slider.scrollLeft >= maxScrollLeft - sliderEdgeTolerance;

  getSliderButtons(slider).forEach((button) => {
    const isPrevious = button.dataset.sliderButton === 'previous';
    button.disabled = maxScrollLeft <= sliderEdgeTolerance || (isPrevious ? isAtStart : isAtEnd);
  });
};

const initializeSlider = (slider) => {
  if (slider.dataset.sliderInitialized === 'true') {
    updateSliderButtons(slider);
    return;
  }

  let updateFrame;
  slider.dataset.sliderInitialized = 'true';
  slider.addEventListener('scroll', () => {
    window.cancelAnimationFrame(updateFrame);
    updateFrame = window.requestAnimationFrame(() => updateSliderButtons(slider));
  }, { passive: true });

  updateSliderButtons(slider);
};

const initializeSliders = (root = document) => {
  root.querySelectorAll('[data-slider]').forEach(initializeSlider);
};

document.addEventListener('click', (event) => {
  const sliderButton = event.target.closest('[data-slider-button]');
  if (sliderButton) {
    if (sliderButton.disabled) return;

    const targetId = sliderButton.dataset.sliderTarget;
    const section = sliderButton.closest('.section-band');
    const slider = targetId
      ? document.getElementById(targetId)
      : section && section.querySelector('[data-slider]');
    if (!slider) return;

    const direction = sliderButton.dataset.sliderButton === 'previous' ? -1 : 1;
    const firstCard = slider.querySelector('.category-card, .product-card');
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : slider.clientWidth;
    const styles = window.getComputedStyle(slider);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const visibleCards = Math.max(1, Math.floor((slider.clientWidth + gap) / (cardWidth + gap)));
    const distance = direction * (cardWidth + gap) * visibleCards;

    if (typeof slider.scrollBy === 'function') {
      slider.scrollBy({ left: distance, behavior: 'smooth' });
    } else {
      slider.scrollLeft += distance;
    }
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeSliders());
} else {
  initializeSliders();
}

window.addEventListener('resize', () => initializeSliders());
document.addEventListener('shopify:section:load', (event) => initializeSliders(event.target));

const cartRoute = (endpoint) => {
  const root = window.Shopify && window.Shopify.routes
    ? window.Shopify.routes.root
    : '/';
  return `${root}${endpoint}`;
};

const updateCartCount = (count) => {
  const normalizedCount = Math.max(0, Number(count) || 0);

  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = normalizedCount;
    badge.hidden = normalizedCount === 0;
  });
};

let cartNotificationTimer;
let cartNotificationHideTimer;
let cartNotificationTransitionHandler;

const finishCartNotificationClose = (notification) => {
  if (!notification.classList.contains('is-closing')) return;

  window.clearTimeout(cartNotificationHideTimer);
  if (cartNotificationTransitionHandler) {
    notification.removeEventListener('transitionend', cartNotificationTransitionHandler);
    cartNotificationTransitionHandler = null;
  }

  notification.hidden = true;
  notification.classList.remove('is-visible', 'is-closing');
};

const closeCartNotification = () => {
  const notification = document.querySelector('[data-cart-notification]');
  if (!notification) return;

  window.clearTimeout(cartNotificationTimer);
  window.clearTimeout(cartNotificationHideTimer);
  if (notification.hidden || notification.classList.contains('is-closing')) return;

  notification.classList.remove('is-visible');
  notification.classList.add('is-closing');

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finishCartNotificationClose(notification);
    return;
  }

  cartNotificationTransitionHandler = (event) => {
    if (event.target === notification && event.propertyName === 'opacity') {
      finishCartNotificationClose(notification);
    }
  };
  notification.addEventListener('transitionend', cartNotificationTransitionHandler);

  // Fallback for browsers that don't emit transitionend after a page loses focus.
  cartNotificationHideTimer = window.setTimeout(() => {
    finishCartNotificationClose(notification);
  }, 400);
};

const showCartNotification = ({ item, message, isError = false }) => {
  const notification = document.querySelector('[data-cart-notification]');
  if (!notification) return;

  const messageElement = notification.querySelector('[data-cart-notification-message]');
  const productElement = notification.querySelector('[data-cart-notification-product]');
  const titleElement = notification.querySelector('[data-cart-notification-title]');
  const imageElement = notification.querySelector('[data-cart-notification-image]');
  const defaultMessage = isError
    ? notification.dataset.errorMessage
    : notification.dataset.successMessage;
  const productTitle = item && (item.product_title || item.title);
  const productImage = item && (
    (item.featured_image && item.featured_image.url)
    || item.image
  );

  const wasVisible = !notification.hidden
    && notification.classList.contains('is-visible')
    && !notification.classList.contains('is-closing');

  notification.classList.toggle('is-error', isError);
  notification.classList.remove('is-closing');
  window.clearTimeout(cartNotificationHideTimer);
  if (cartNotificationTransitionHandler) {
    notification.removeEventListener('transitionend', cartNotificationTransitionHandler);
    cartNotificationTransitionHandler = null;
  }
  if (messageElement) messageElement.textContent = message || defaultMessage || '';

  if (productElement) {
    productElement.hidden = isError || !productTitle;
  }

  if (titleElement) {
    titleElement.textContent = productTitle || '';
  }

  if (imageElement) {
    imageElement.hidden = !productImage;
    if (productImage) {
      imageElement.src = productImage;
      imageElement.alt = productTitle || '';
    } else {
      imageElement.removeAttribute('src');
      imageElement.alt = '';
    }
  }

  notification.hidden = false;
  if (wasVisible) {
    notification.classList.add('is-visible');
  } else {
    notification.classList.remove('is-visible');
    window.requestAnimationFrame(() => {
      if (!notification.hidden && !notification.classList.contains('is-closing')) {
        notification.classList.add('is-visible');
      }
    });
  }
  window.clearTimeout(cartNotificationTimer);
  cartNotificationTimer = window.setTimeout(closeCartNotification, 5000);
};

const getCart = async () => {
  const response = await fetch(cartRoute('cart.js'), {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('Unable to retrieve cart.');
  return response.json();
};

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-cart-add-form], .product-form');
  if (!form) return;

  event.preventDefault();

  const submitButton = event.submitter || form.querySelector('button[name="add"]');
  if (submitButton && submitButton.disabled) return;

  const formData = new FormData(form);
  const addedQuantity = Math.max(1, Number(formData.get('quantity')) || 1);
  const currentCount = Number(document.querySelector('[data-cart-count]')?.textContent) || 0;

  form.setAttribute('aria-busy', 'true');
  if (submitButton) submitButton.disabled = true;

  try {
    const response = await fetch(cartRoute('cart/add.js'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const item = await response.json();

    if (!response.ok) {
      throw new Error(item.description || item.message);
    }

    updateCartCount(currentCount + addedQuantity);
    showCartNotification({ item });

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = 1;

    try {
      const cart = await getCart();
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      // The optimistic count remains visible if the follow-up request fails.
    }
  } catch (error) {
    showCartNotification({
      message: error.message,
      isError: true,
    });
  } finally {
    form.removeAttribute('aria-busy');
    if (submitButton) submitButton.disabled = false;
  }
});

const cartUpdateTimers = new WeakMap();

const setCartPageBusy = (page, isBusy) => {
  page.toggleAttribute('aria-busy', isBusy);
  page.querySelectorAll('[data-cart-control]').forEach((control) => {
    control.disabled = isBusy;
  });
};

const renderCartSection = (page, cart) => {
  const sectionId = page.dataset.sectionId;
  const sectionHtml = cart.sections && cart.sections[sectionId];
  const currentSection = document.getElementById(`shopify-section-${sectionId}`);

  if (!sectionHtml || !currentSection) return false;

  const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const replacementSection = parsedDocument.getElementById(`shopify-section-${sectionId}`);

  if (replacementSection) {
    currentSection.replaceWith(replacementSection);
  } else {
    currentSection.innerHTML = sectionHtml;
  }

  return true;
};

const updateCartLine = async (line, quantity) => {
  const page = line.closest('[data-cart-page]');
  if (!page) return;

  const input = line.querySelector('[data-cart-quantity-input]');
  const preservedCartFields = [...page.querySelectorAll('[data-cart-preserve]')].map((field) => ({
    key: field.dataset.cartPreserve,
    value: field.value,
  }));
  const errorElement = page.querySelector('[data-cart-error]');
  const sectionId = page.dataset.sectionId;
  const normalizedQuantity = Math.max(0, Number(quantity) || 0);

  if (errorElement) {
    errorElement.hidden = true;
    errorElement.textContent = '';
  }

  setCartPageBusy(page, true);

  try {
    const response = await fetch(cartRoute('cart/change.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: line.dataset.lineKey,
        quantity: normalizedQuantity,
        sections: [sectionId],
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();

    if (!response.ok) {
      throw new Error(cart.description || cart.message);
    }

    updateCartCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

    if (!renderCartSection(page, cart)) {
      window.location.reload();
    } else {
      preservedCartFields.forEach(({ key, value }) => {
        const renderedField = document.querySelector(`[data-cart-page] [data-cart-preserve="${key}"]`);
        if (renderedField) renderedField.value = value;
      });
    }
  } catch (error) {
    setCartPageBusy(page, false);
    if (input) input.value = line.dataset.lineQuantity;
    if (errorElement) {
      errorElement.textContent = error.message || page.dataset.updateError;
      errorElement.hidden = false;
    }
  }
};

const scheduleCartLineUpdate = (line, quantity, delay = 280) => {
  const existingTimer = cartUpdateTimers.get(line);
  if (existingTimer) window.clearTimeout(existingTimer);

  const timer = window.setTimeout(() => {
    cartUpdateTimers.delete(line);
    updateCartLine(line, quantity);
  }, delay);
  cartUpdateTimers.set(line, timer);
};

document.addEventListener('click', (event) => {
  const closeButton = event.target.closest('[data-cart-notification-close]');
  if (closeButton) {
    closeCartNotification();
    return;
  }

  const quantityButton = event.target.closest('[data-cart-quantity-action]');
  if (quantityButton) {
    const line = quantityButton.closest('[data-cart-line]');
    const input = line && line.querySelector('[data-cart-quantity-input]');
    if (!line || !input) return;

    const direction = quantityButton.dataset.cartQuantityAction === 'increase' ? 1 : -1;
    const nextQuantity = Math.max(1, (Number(input.value) || 1) + direction);
    input.value = nextQuantity;
    scheduleCartLineUpdate(line, nextQuantity);
    return;
  }

  const removeButton = event.target.closest('[data-cart-remove]');
  if (removeButton) {
    const line = removeButton.closest('[data-cart-line]');
    if (!line) return;

    const existingTimer = cartUpdateTimers.get(line);
    if (existingTimer) window.clearTimeout(existingTimer);
    cartUpdateTimers.delete(line);
    updateCartLine(line, 0);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quantity-input]');
  if (!input) return;

  const line = input.closest('[data-cart-line]');
  if (!line) return;

  const quantity = Math.max(1, Number(input.value) || 1);
  input.value = quantity;
  scheduleCartLineUpdate(line, quantity);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeCartNotification();
});
